import { SearchInput } from "components/Search/SearchInput"
import { useNumber, useString, useWindowSize } from "helpers/hooks"
import { useCallback, useEffect, useState } from "react"
import {
  AssignmentProjectQueryState,
  AssignmentProjectResponse,
} from "../user.type"
import SimpleTable, { SimpleTableProps } from "components/Table/SimpleTable"
import { cloneDeep, debounce } from "lodash"
import { Pagination } from "components/Pagination/Pagination"
import { getAssignmentProjectByUserIdMiddleware } from "../api.service"
import { postUnassignProjectFromGroupsOrganizationMiddleware } from "pages/groups/groups.api"
import { formatDate, getPrivateKeyByUserId } from "helpers/utils"
import { ReactComponent as IconCloseBlack } from "assets/images/icons/icon-close-black.svg"
import Tippy from "@tippyjs/react"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { toast } from "react-toastify"
import { ActivityLogType } from "helpers/activity_log"
import {
  EncryptionKeys,
  encryptionController,
} from "controllers/EncryptionController"
import { activityLogHelper } from "helpers/activity_log"
import { postActionLogMiddleware } from "components/activity-log/activity-log.api"
import { KeyEncryptionType } from "types"
import { encryptionGCMHelper } from "helpers/encryption-gcm"

interface GroupTabProps {
  userId: string
  isAdmin: boolean
}

export const ProjectTab = (props: GroupTabProps) => {
  const { userId, isAdmin } = props
  const { isMobile } = useWindowSize()
  const search = useString()
  const [projects, setProjects] = useState<AssignmentProjectResponse[]>([])
  const totalPage = useNumber(0)
  const totalLabel = useString("")
  const [queryState, setQueryState] = useState<AssignmentProjectQueryState>({
    page: 1,
    pageSize: 25,
  })

  useEffect(() => {
    getProjects(queryState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryState.pageSize,
    queryState.page,
    queryState.search,
    queryState.sort,
    queryState.order,
  ])

  const getProjects = async (newQueyState: AssignmentProjectQueryState) => {
    const response = await getAssignmentProjectByUserIdMiddleware(
      newQueyState,
      userId || ""
    )
    setProjects(response.data)
    totalPage.setValue(response.total)
    totalLabel.setValue(response.totalLabel)
  }

  const debounceSearch = useCallback(
    debounce((newSearch: string, newQueyState: AssignmentProjectQueryState) => {
      setQueryState({
        ...newQueyState,
        search: newSearch.trim(),
        page: 1,
      })
    }, 500),
    []
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      search.setValue(value)
      debounceSearch(value, cloneDeep(queryState))
    },
    [debounceSearch, queryState, search]
  )

  const handleSearchClear = useCallback(() => {
    search.setValue("")
    setQueryState({
      ...queryState,
      search: "",
    })
  }, [queryState, search])

  const handleChangePage = (newPage: number, newLimit: number) => {
    setQueryState({
      ...queryState,
      page: newPage,
      pageSize: newLimit,
    })
  }

  const handleSort = (
    dataIndex: string,
    sortDirection: "asc" | "desc" | null
  ) => {
    setQueryState({
      ...queryState,
      sort: sortDirection ? dataIndex : "",
      order: sortDirection || "",
      page: 1, // Reset to first page when sorting
    })
  }
  const handleUnassignGroup = (columnData: AssignmentProjectResponse) => {
    const handleSubmit = async () => {
      try {
        await postUnassignProjectFromGroupsOrganizationMiddleware(columnData.id)
        let decrypted = columnData.projectKey
        const privateKeyPem: any = await getPrivateKeyByUserId()
        const userVaultKey = localStorage.getItem(
          EncryptionKeys.userVaultKey
        ) as string
        if (
          columnData.projectKey &&
          columnData.projectKeyType === KeyEncryptionType.ProjectShareKey
        ) {
          decrypted = privateKeyPem.decrypt(columnData.projectKey, "RSA-OAEP")
        } else {
          decrypted = await encryptionGCMHelper.decryptContext(
            userVaultKey,
            columnData.projectKey
          )
        }
        if (decrypted !== columnData.projectKey) {
          const content = await encryptionController().encrypt(
            activityLogHelper.toLogMessage(
              ActivityLogType.UnAssignProjectFromUser,
              {
                userName: columnData.userEmail,
              }
            ),
            {
              dataType: "string",
              type: "project",
              encryptionKey: decrypted,
            }
          )
          await postActionLogMiddleware(
            {
              content: content,
              created_by: localStorage.getItem("user_id") || "",
              model: "projects",
              relation_id: columnData.assignment_project_id,
            },
            false
          )
        }

        if (queryState.page === 1) {
          getProjects(queryState)
        } else {
          setQueryState({
            ...queryState,
            page: 1,
          })
        }
        toast(
          <LabelNotificationPage
            messenger="Unassign successfully!"
            type="success"
          />
        )
        closeModal()
      } catch (error) {
        closeModal()
        toast(
          <LabelNotificationPage messenger="Unassign failed!" type="error" />
        )
      }
    }

    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title:
              "Unassigning this member from the project will revoke all user-specific permissions and settings associated with the project.",
            label: "Unassign",
            content: "This action cannot be undone!",
            styleTitle: { textAlign: "center" },
            titleButton: "Unassign",
            colorYellowButton: false,
            onSubmit: handleSubmit,
          },
        },
      })
    )
  }
  const columnsMobile: SimpleTableProps["columns"] = [
    {
      title: "Project",
      dataIndex: "name",
      render: (columnData: AssignmentProjectResponse) => {
        return (
          <div className="flex flex-col gap-[8px]">
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Project:{" "}
              <span className="font-semibold">{`${columnData.code} - ${columnData.name}`}</span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Created:{" "}
              <span className="">
                {columnData.created_at
                  ? formatDate(columnData.created_at, "DD MMM yyyy - HH:mm A")
                  : "-"}
              </span>
            </p>
          </div>
        )
      },
    },
  ]

  const columns: SimpleTableProps["columns"] = [
    {
      title: "Project",
      dataIndex: "name",
      render: (columnData: AssignmentProjectResponse) => (
        <p className="pl-[20px]">{`${columnData.code} - ${columnData.name}`}</p>
      ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      sortable: true,
      render: (columnData: AssignmentProjectResponse) => (
        <p
          className=" text-[13px] leading-[22px] text-[#111111]"
          style={{
            whiteSpace: "nowrap",
          }}
        >
          {columnData.created_at
            ? formatDate(columnData.created_at, "DD MMM yyyy - HH:mm A")
            : "-"}
        </p>
      ),
      width: `200px`,
    },
    {
      title: "",
      dataIndex: "action",
      align: "right",
      width: "100px",
      render: (columnData: AssignmentProjectResponse) => {
        if (!isAdmin) {
          return null
        }
        return (
          <div className="flex items-center justify-end">
            <Tippy
              className="custom-tippy-menu-left flex-1"
              arrow={false}
              placement="bottom"
              content={
                <span
                  className="font-normal"
                  style={{
                    fontSize: 13,
                    lineHeight: "22px",
                  }}
                >
                  Unassign
                </span>
              }
            >
              <div
                onClick={() => handleUnassignGroup(columnData)}
                className="cursor-pointer flex items-center justify-center ml-[16px] bg-[#EA454526] rounded-[4px]"
                style={{
                  width: 26,
                  height: 26,
                }}
              >
                <IconCloseBlack className="icon-close-red" />
              </div>
            </Tippy>
          </div>
        )
      },
    },
  ]
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center justify-between bg-[#FFFFFF] border-b border-[#E4E4E4] min-h-[63px] px-[24px]">
        <h2 className="text-[16px] font-semibold lh-26 text-black">
          {`Projects ${totalLabel.value ? `(${totalLabel.value})` : ""}`}
        </h2>
        <div className="flex items-center gap-[12px]">
          {isMobile ? null : (
            <SearchInput
              value={search.value}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              placeholder="Search by project code or name"
            />
          )}
        </div>
      </div>
      <div className="flex flex-col py-6 pl-6 flex-1 overflow-hidden">
        <div className="flex flex-col pr-6 overflow-auto">
          <div className="text-black bg-white border border-border-gray rounded-md custom-table pb-[6px]">
            <SimpleTable
              customClass="storage-usage-table"
              columns={isMobile ? columnsMobile : columns}
              dataSource={projects || []}
              isRequiredTableHeader={isMobile ? false : true}
              onSort={handleSort}
            />
          </div>
          {totalPage.value ? (
            <Pagination
              total={totalPage.value}
              queryState={{
                page: queryState.page,
                limit: queryState.pageSize,
              }}
              handleChangePage={handleChangePage}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
